import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate } from "../lib/utils";
import {
  Check, Star, Sparkle, ArrowRight, Clock, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_ORDER = ["start", "pro", "elite"];
const PLAN_FEATURE_KEYS = {
  start: ["su_feat_start_1", "su_feat_start_2", "su_feat_start_3", "su_feat_start_4", "su_feat_start_5"],
  pro: ["su_feat_pro_0", "su_feat_pro_1", "su_feat_pro_2", "su_feat_pro_3", "su_feat_pro_4", "su_feat_pro_5"],
  elite: ["su_feat_elite_0", "su_feat_elite_1", "su_feat_elite_2", "su_feat_elite_3", "su_feat_elite_4", "su_feat_elite_5"],
};

export default function ClientSubscription() {
  const { t, lang } = useI18n();
  const [plans, setPlans] = useState({});
  const [current, setCurrent] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(null);

  const load = async () => {
    const p = await api.get("/subscriptions/plans");
    setPlans(p.data);
    const c = await api.get("/client/subscription");
    setCurrent(c.data);
    if (c.data?.period) setPeriod(c.data.period);
  };
  useEffect(() => { load(); }, []);

  const currentPlanRank = PLAN_ORDER.indexOf(current?.plan) ;
  const scheduledChange = current?.scheduled_change;

  const changeOrSubscribe = async (planKey) => {
    setLoading(planKey);
    try {
      if (current) {
        // Cambio piano con proration
        const { data } = await api.post("/client/subscription/change", {
          plan: planKey, period, origin_url: window.location.origin,
        });
        if (data.kind === "upgrade_prorata" && data.url) {
          window.location.href = data.url;
          return;
        }
        if (data.kind === "downgrade_scheduled") {
          toast.success(data.message || t("sub_downgrade_scheduled"));
          await load();
          setLoading(null);
          return;
        }
        if (data.url) {
          window.location.href = data.url;
          return;
        }
      } else {
        // Prima sottoscrizione: checkout classico
        const { data } = await api.post("/client/subscriptions/checkout", {
          plan: planKey, period, origin_url: window.location.origin,
        });
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dash_stripe_error"));
      setLoading(null);
    }
  };

  const cancelScheduledChange = async () => {
    try {
      await api.post("/client/subscription/cancel-scheduled-change");
      toast.success(t("sub_change_cancelled"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    }
  };

  const planEntries = PLAN_ORDER.filter((k) => plans[k]).map((k) => [k, plans[k]]);
  const yearly = period === "yearly";

  return (
    <div className="space-y-8" data-testid="client-subscription">
      <div>
        <div className="tf-label">{t("nav_subscription")}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-1">{t("sub_page_title")}</h1>
        <p className="text-[var(--tf-muted)] mt-2 max-w-2xl">
          {t("sub_page_subtitle")}
        </p>
      </div>

      {current && (
        <div className="tf-card p-5 border-2 border-[var(--tf-primary)] bg-[#F5FBE8]" data-testid="current-sub">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 tf-label text-[#193529] bg-[#CCF22C] px-2.5 py-1 rounded-full">
                <Sparkle size={10} weight="fill" /> {t("sub_your_plan")}
              </div>
              <div className="font-display font-bold text-xl mt-2">
                {plans[current.plan]?.name || current.plan} · {current.period === "yearly" ? t("sub_yearly") : t("sub_monthly")}
              </div>
              <div className="text-sm text-[var(--tf-muted)] mt-1">
                {t("sub_expires")} <span className="tf-numeric text-[var(--tf-ink)] font-medium">{fmtDate(current.expires_at, lang).split(",")[0]}</span>
                {" · "}
                {t("sub_pod_discount_active")} <span className="font-bold text-[#4A8A3E]">{plans[current.plan]?.pod_discount_pct || 0}%</span>
              </div>
            </div>
            <div className="text-right">
              <div className="tf-label">{t("sub_amount_paid")}</div>
              <div className="font-display font-bold text-2xl tf-numeric">{fmtEur(current.amount)}</div>
            </div>
          </div>

          {scheduledChange && (
            <div className="mt-4 pt-4 border-t border-[#CCF22C] flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-2 text-sm text-[var(--tf-muted)]">
                <Clock size={16} weight="fill" className="text-[#D4A017] mt-0.5" />
                <div>
                  {t("sub_scheduled_downgrade", {
                    plan: plans[scheduledChange.plan]?.name || scheduledChange.plan,
                    period: scheduledChange.period === "yearly" ? t("sub_yearly") : t("sub_monthly"),
                    date: scheduledChange.effective_at?.slice(0, 10),
                  })}
                </div>
              </div>
              <button
                onClick={cancelScheduledChange}
                className="tf-btn tf-btn-ghost !py-1.5 !px-3 text-xs"
                data-testid="cancel-scheduled-change"
              >
                <XIcon size={12} weight="bold" /> {t("sub_cancel_change")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Period toggle */}
      <div className="flex justify-center">
        <div className="inline-flex p-1 rounded-full border border-[var(--tf-border)] bg-white" data-testid="period-toggle">
          <button
            onClick={() => setPeriod("monthly")}
            className={`px-6 py-2 rounded-full text-sm font-semibold transition ${period === "monthly" ? "bg-[var(--tf-ink)] text-white" : "text-[var(--tf-muted)]"}`}
            data-testid="toggle-monthly"
          >
            {t("sub_monthly")}
          </button>
          <button
            onClick={() => setPeriod("yearly")}
            className={`px-6 py-2 rounded-full text-sm font-semibold transition flex items-center gap-2 ${period === "yearly" ? "bg-[var(--tf-ink)] text-white" : "text-[var(--tf-muted)]"}`}
            data-testid="toggle-yearly"
          >
            {t("sub_yearly")}
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${period === "yearly" ? "bg-[var(--tf-primary)] text-[var(--tf-ink)]" : "bg-[var(--tf-primary)]/30 text-[var(--tf-ink)]"}`}>-20%</span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-6">
        {planEntries.map(([key, p]) => {
          const price = yearly ? p.yearly_price : p.monthly_price;
          const monthlyEquivalent = yearly ? (p.yearly_price / 12).toFixed(2) : null;
          const isActive = current?.plan === key && current?.period === period;
          const recommended = p.recommended;
          const targetRank = PLAN_ORDER.indexOf(key);
          let ctaLabel = t("sub_subscribe");
          let ctaKind = "primary";
          if (current) {
            if (isActive) {
              ctaLabel = t("sub_active_plan");
            } else if (targetRank > currentPlanRank || (targetRank === currentPlanRank && period === "yearly" && current.period === "monthly")) {
              ctaLabel = t("sub_upgrade");
              ctaKind = "primary";
            } else {
              ctaLabel = t("sub_switch_plan");
              ctaKind = "dark";
            }
          }
          return (
            <div
              key={key}
              className={`tf-card p-7 flex flex-col relative ${isActive ? "border-2 border-[var(--tf-primary)] bg-[#F5FBE8]" : recommended ? "border-2 border-[var(--tf-ink)]" : ""}`}
              data-testid={`plan-${key}`}
            >
              {isActive && (
                <div className="absolute -top-3 left-7 px-3 py-1 bg-[var(--tf-primary)] text-[var(--tf-ink)] text-[10px] font-black uppercase tracking-widest rounded-full inline-flex items-center gap-1">
                  <Sparkle size={10} weight="fill" /> {t("sub_your_plan")}
                </div>
              )}
              {!isActive && recommended && (
                <div className="absolute -top-3 left-7 px-3 py-1 bg-[var(--tf-ink)] text-white text-[10px] font-bold uppercase tracking-widest rounded-full inline-flex items-center gap-1">
                  <Star size={10} weight="fill" /> {t("sub_most_chosen")}
                </div>
              )}

              <div className="tf-label">{p.name.toUpperCase()}</div>
              <div className="font-display text-5xl font-black tracking-tight mt-3 tf-numeric">
                {fmtEur(price)}
              </div>
              <div className="text-sm text-[var(--tf-muted)] mt-1">
                {yearly ? t("sub_per_month_equiv", { amount: fmtEur(monthlyEquivalent) }) : t("sub_per_month")}
              </div>
              {yearly && (
                <div className="mt-2 text-xs font-semibold text-[var(--tf-success)]">
                  {t("sub_save_amount_year", { amount: fmtEur(p.monthly_price * 12 - p.yearly_price) })}
                </div>
              )}
              {p.pod_discount_pct > 0 && (
                <div className="mt-2 text-xs font-semibold text-[#4A8A3E]">
                  {t("sub_pod_discount", { pct: p.pod_discount_pct })}
                </div>
              )}

              <ul className="space-y-3 mt-7 mb-7 flex-1">
                {(PLAN_FEATURE_KEYS[key] || []).map((fk, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <div className="mt-0.5 w-5 h-5 rounded-full bg-[var(--tf-primary)] flex-shrink-0 flex items-center justify-center">
                      <Check size={12} weight="bold" style={{ color: "var(--tf-ink)" }} />
                    </div>
                    <span>{t(fk)}</span>
                  </li>
                ))}
              </ul>

              {isActive ? (
                <div className="tf-btn tf-btn-ghost w-full !cursor-default" data-testid={`active-${key}`}>
                  <Star size={14} weight="fill" /> {ctaLabel}
                </div>
              ) : (
                <button
                  onClick={() => changeOrSubscribe(key)}
                  disabled={loading === key}
                  className={`tf-btn w-full py-3 ${ctaKind === "primary" ? "tf-btn-primary" : "tf-btn-dark"}`}
                  data-testid={`subscribe-${key}`}
                >
                  {loading === key ? "..." : <>{ctaLabel} <ArrowRight size={14} weight="bold" /></>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center text-xs text-[var(--tf-muted)] max-w-2xl mx-auto">
        {t("sub_upgrade_note")}
        <br />
        {t("sub_downgrade_note")}
        <br />
        {t("sub_secure_payments_note")}
      </div>
    </div>
  );
}
